import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Center, Flex, Spinner, Stack, Text } from '@chakra-ui/react';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import { formatValue } from 'react-currency-input-field';

import {
  DocumentDownloadOutline,
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  useToast,
} from '@aslanpay/portal-common-lib';

import DeductionAdjustmentsList from 'app/components/DeductionAdjustments/DeductionAdjustmentsList';
import { Table } from 'app/components/UI/Table/Table';
import { IndividualDeductionsReportActions } from 'app/components/UI/Table/Table.types';

import { defaultPageSize } from 'app/constants/table';

import useExportTransactions from 'app/hooks/useExportTransactions';
import useFetchIndividualDeductionsReportsPage from 'app/hooks/useFetchIndividualDeductionsReportsPage';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';

import { useManagingCompanyStore } from 'app/store';

import { getIndividualDeductionsReportProfilePath } from 'app/utils/routes';
import getIndividualDeductionsReportsColumns from 'app/utils/table/individualDeductionsReports';

import { IndividualDeductionsReportsTabProps } from './types';

import type { Filter } from '@aslanpay/portal-common-lib';

function IndividualDeductionsReportsTab({ individual }: IndividualDeductionsReportsTabProps) {
  const toast = useToast();

  const navigate = useNavigate();

  const { company } = useManagingCompanyStore.getState();

  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [payCycleFilter, setPayCycleFilter] = useState<string[]>(
    searchParams.get('payCycles') ? searchParams.get('payCycles')?.split(',') || [] : []
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);

  const resetPagination = () => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const { data: payCalendar, isLoading: isLoadingPayCalendar } = useFetchPayCalendar(
    individual?.payCalendarId
  );

  const { data: reports, isLoading: isLoadingReports } = useFetchIndividualDeductionsReportsPage({
    individualIds: [individual?.id || ''],
    payCycleIds: payCycleFilter,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const payCyclesOptions: DropdownOption[] =
    payCalendar?.payCycles.map((payCycle) => {
      const text = format(parseISO(payCycle.startDate as string), 'MMMM yyyy');
      return {
        value: payCycle.id,
        text,
      };
    }) || [];

  const salaryDeductionsSum =
    reports?.list.map((report) => report.salaryDeductions).reduce((sum, value) => sum + value, 0) ||
    0;

  const actions: IndividualDeductionsReportActions = {
    select: (id: string) => navigate(getIndividualDeductionsReportProfilePath(id)),
  };

  const filters: Filter[] = [
    {
      name: 'Pay cycle',
      type: 'category',
      data: payCyclesOptions,
      selectedValues: payCycleFilter,
    },
  ];

  const { mutate: exportTransactions } = useExportTransactions({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Transactions report downloaded',
      });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to download the file. Please try again.',
      });
    },
  });

  const handlePayCycleChange = (values: (string | number)[]) => {
    setPayCycleFilter(values as string[]);
    resetPagination();
  };

  const handleFilterChange = (_: string, newValues: string[]) => {
    handlePayCycleChange(newValues);
  };

  const handleFilterClick = (filterName: string) => {
    setActiveFilter(filterName);
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) => {
    return (
      (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
      String(filterValue)
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    params.set('page', (pageIndex + 1).toString());

    if (payCycleFilter.length > 0) {
      params.set('payCycles', payCycleFilter.join(','));
    } else {
      params.delete('payCycles');
    }

    if (sorting[0]) {
      params.set('sortBy', sorting[0].id);
      params.set('sort', sorting[0].desc ? 'DESC' : 'ASC');
    }

    const newParamsString = params.toString();

    if (newParamsString !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [pageIndex, sorting, payCycleFilter, setSearchParams, searchParams]);

  return (
    <Flex flexDir="column" w="full" gap="20px">
      {isLoadingReports ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <>
          <Flex align="center" justify="space-between">
            <Text
              p="8px"
              borderRadius="10px"
              bgColor="alert.error"
              color="system.error"
              textStyle="extraSmallBold"
            >
              These totals only apply to the current page.
            </Text>
            <Button
              leftIcon={<DocumentDownloadOutline boxSize="24px" />}
              size="md"
              onClick={() => exportTransactions({ individualIds: [individual?.id || ''] })}
            >
              Download all transactions
            </Button>
          </Flex>

          <Flex
            px="20px"
            py="10px"
            gap="60px"
            border="1px solid"
            borderRadius="10px"
            borderColor="brand.lightGray1"
          >
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Total deductions amount
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {formatValue({
                  value: salaryDeductionsSum.toString(),
                  prefix: '\u00A3',
                  groupSeparator: ',',
                  decimalSeparator: '.',
                  decimalScale: 2,
                })}
              </Text>
            </Stack>
          </Flex>
        </>
      )}
      <Flex>
        {isLoadingPayCalendar ? (
          <Center width="full" height="full" flexGrow={1}>
            <Spinner color="brand.purple" size="xl" mb="50px" />
          </Center>
        ) : (
          <Filters
            activeFilter={activeFilter}
            displayFormatter={formatFilterValue}
            filters={filters}
            onFilterClose={() => setActiveFilter(undefined)}
            onFilterChange={handleFilterChange}
            onFilterClick={handleFilterClick}
          />
        )}
      </Flex>
      {isLoadingReports ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getIndividualDeductionsReportsColumns(actions, false, !company?.id)}
          data={reports?.list || []}
          total={reports?.total || 0}
          pageNumber={reports?.pageNumber || pageIndex}
          pageSize={reports?.pageSize || pageSize}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          getRowId={(report) => report.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
        />
      )}
      {individual?.id && <DeductionAdjustmentsList individualIds={[individual.id]} />}
    </Flex>
  );
}

export default IndividualDeductionsReportsTab;
